import * as THREE from 'three';
import { disposeBatch, type BatchInstance, type LayerBatch, type LayerBatches } from './batch';
import { edgesMaterial, layerEdges } from './edges';
import { presetMaps, presetOf } from './materials';
import { BatchMaterial, layerUniforms, type BatchPass } from './nodes';

export type { ElementEntry } from './batch';

export type ElementState =
  | 'planned'
  | 'active'
  | 'stage0'
  | 'stage1'
  | 'stage2'
  | 'stage3'
  | 'stage4'
  | 'pass'
  | 'fail'
  | 'failSoft'
  | 'highlight'
  | 'dim';

interface StateSpec {
  color: string;
  emissive?: string;
  emissiveIntensity?: number;
  opacity?: number;
}

const STATE_SPECS: Record<ElementState, StateSpec> = {
  planned: { color: '#1b1f27', opacity: 0.28 },
  active: { color: '#f0b429', emissive: '#f0b429', emissiveIntensity: 0.9 },
  stage0: { color: '#3a3f4a', opacity: 0.3 },
  stage1: { color: '#f0b429', opacity: 0.45, emissive: '#f0b429', emissiveIntensity: 0.25 },
  stage2: { color: '#4cc9f0', opacity: 0.45, emissive: '#4cc9f0', emissiveIntensity: 0.25 },
  stage3: { color: '#7bd88f', opacity: 0.5, emissive: '#7bd88f', emissiveIntensity: 0.3 },
  stage4: { color: '#e8fff0', opacity: 0.6, emissive: '#7bd88f', emissiveIntensity: 0.6 },
  pass: { color: '#7bd88f', opacity: 0.35, emissive: '#3fae6a', emissiveIntensity: 0.3 },
  fail: { color: '#e0453a', opacity: 0.8, emissive: '#e0453a', emissiveIntensity: 0.4 },
  failSoft: { color: '#e0453a', opacity: 0.35, emissive: '#e0453a', emissiveIntensity: 0.3 },
  highlight: { color: '#4cc9f0', emissive: '#4cc9f0', emissiveIntensity: 1.1 },
  dim: { color: '#14171d', opacity: 0.35 },
};

// What one instance shows instead of its own look: batch colour (rgba) and state texel.
interface Look {
  color: THREE.Vector4;
  state: THREE.Vector4;
}

function lookOf(color: string, opacity: number, emissive?: string, strength = 0): Look {
  const c = new THREE.Color(color);
  const e = new THREE.Color(emissive ?? '#000000');
  return {
    color: new THREE.Vector4(c.r, c.g, c.b, opacity),
    state: new THREE.Vector4(e.r, e.g, e.b, emissive ? strength : 0),
  };
}

const STATE_LOOKS = Object.fromEntries(
  Object.entries(STATE_SPECS).map(([state, s]) => [
    state,
    lookOf(s.color, s.opacity ?? 1, s.emissive, s.emissiveIntensity),
  ]),
) as Record<ElementState, Look>;
const OWN_STATE = new THREE.Vector4(0, 0, 0, -1);
const SPACE_COLOR = lookOf('#4cc9f0', 0.18).color;

interface BatchLook {
  solid: BatchMaterial;
  glass: BatchMaterial | null;
  state: THREE.DataTexture;
  data: Float32Array;
  translucent: Set<number>;
  split: boolean;
}

// Everything a station may do to a layer's look: world-space clip plane (build-up), uGhost fresnel
// shell on arch/structure with emissive lift on MEP (x-ray), per-element state and colour
// written per instance into the batch colour and state textures.
export interface LayerRig {
  setClipY(y: number): void;
  // a second world-space plane next to the horizontal one, for vertical sections
  setClipPlane(plane: THREE.Plane | null): void;
  setGhost(g: number): void;
  setVisible(v: boolean): void;
  setUserHidden(h: boolean): void;
  setTint(color: THREE.ColorRepresentation | null, amount: number): void;
  // 0 edges only, 1 flat IFC colours, 2 material presets; between 1 and 2 it blends
  setReveal(stage: number): void;
  setElementState(expressID: number, state: ElementState | null): void;
  clearStates(): void;
  setElementColor(expressID: number, hex: string | null): void;
  clearColors(): void;
  setElementVisible(expressID: number, visible: boolean): void;
  clearVisibility(): void;
  // elements the visitor deleted from the model: hidden until they come back, whatever a
  // station shows or clears
  setRemoved(ids: Iterable<number>): void;
  // adopts batches a rebatch added and repaints the ones it refilled
  refresh(): void;
  readonly elements: LayerBatches['elements'];
  readonly storeys: string[];
  dispose(): void;
}

export function rigLayer(
  layer: LayerBatches,
  role: 'shell' | 'revealed' | 'space',
  metallicDucts: boolean,
): LayerRig {
  // clipping lives on the layer's ClippingGroup, not on the materials
  const plane = new THREE.Plane(new THREE.Vector3(0, -1, 0), 0);
  const cut = new THREE.Plane();
  let cutOn = false;
  layer.root.clippingPlanes = [plane];
  const uniforms = layerUniforms();
  const perBatch = new Map<LayerBatch, BatchLook>();
  const materials = new Map<BatchMaterial, BatchPass>();
  const painted = new Map<number, Look>();
  const current = new Map<number, ElementState | null>();
  const colorLooks = new Map<string, Look>();
  const colored = new Set<number>();
  const hiddenElements = new Set<number>();
  const removed = new Set<number>();
  const shows = (expressID: number) => !hiddenElements.has(expressID) && !removed.has(expressID);
  let stationVisible = true;
  let userHidden = false;
  let lastGhost = 0;
  let reveal = 2;
  let edges: THREE.LineSegments[] | null = null;
  const edgeMaterial = edgesMaterial();
  const { elements, storeys } = layer;

  // Spaces and the pipeline's transparent materials are translucent as a whole: one pass.
  const wholeGlass = (b: LayerBatch) => b.glass || role === 'space';

  const material = (b: LayerBatch, pass: BatchPass, state: THREE.Texture) => {
    const src = b.source as THREE.MeshStandardMaterial;
    // MEP reads best as brushed metal under the env light; only MEP gets the emissive lift,
    // glowing furniture reads as noise.
    const metal = metallicDucts && role === 'revealed';
    const lift = role !== 'shell' && metallicDucts;
    const preset = presetOf(b.key);
    const m = new BatchMaterial(uniforms, state, {
      pass,
      preset,
      maps: presetMaps(preset),
      color: new THREE.Color('#ffffff'),
      opacity: 1,
      roughness: metal ? 0.35 : (src.roughness ?? 0.85),
      metalness: metal ? 0.55 : (src.metalness ?? 0),
      emissive: new THREE.Color(lift ? '#ffffff' : '#000000'),
      emissiveIntensity: lift ? 0.3 * lastGhost : 0,
    });
    m.transparent = pass !== 'solid';
    if (pass !== 'solid') m.depthWrite = false;
    if (role === 'shell' && pass === 'solid') {
      m.transparent = lastGhost > 0.001;
      m.depthWrite = lastGhost < 0.5;
    }
    materials.set(m, pass);
    return m;
  };

  const split = (b: LayerBatch, bl: BatchLook) => {
    const want = bl.translucent.size > 0;
    if (want === bl.split) return;
    bl.split = want;
    if (want && !bl.glass) bl.glass = material(b, 'glass', bl.state);
    if (want && b.geometry.groups.length !== 2) {
      // both groups span everything; the multi-draw ranges decide what is drawn
      const count = b.geometry.index?.count ?? 0;
      b.geometry.clearGroups();
      b.geometry.addGroup(0, count, 0);
      b.geometry.addGroup(0, count, 1);
    }
    setMaterial(b, want ? [bl.solid, bl.glass!] : bl.solid);
  };

  const write = ({ batch, id }: BatchInstance, look: Look | undefined) => {
    const bl = perBatch.get(batch);
    if (!bl) return;
    const color = look?.color ?? (role === 'space' ? SPACE_COLOR : batch.baseColors[id]!);
    batch.setColorAt(id, color);
    (look?.state ?? OWN_STATE).toArray(bl.data, id * 4);
    bl.state.needsUpdate = true;
    if (wholeGlass(batch)) return;
    if (color.w < 0.999) bl.translucent.add(id);
    else bl.translucent.delete(id);
    split(batch, bl);
  };

  const repaint = (b: LayerBatch, bl: BatchLook) => {
    bl.translucent.clear();
    b.expressIDs.forEach((expressID, id) => {
      write({ batch: b, id }, painted.get(expressID));
      b.setVisibleAt(id, shows(expressID));
    });
    split(b, bl);
  };

  const adopt = (b: LayerBatch) => {
    const size = Math.ceil(Math.sqrt(b.maxInstanceCount));
    const data = new Float32Array(size * size * 4);
    const state = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.FloatType);
    state.needsUpdate = true;
    const solid = material(b, wholeGlass(b) ? 'all' : 'solid', state);
    const bl: BatchLook = { solid, glass: null, state, data, translucent: new Set(), split: false };
    perBatch.set(b, bl);
    setMaterial(b, solid);
    repaint(b, bl);
  };

  const release = (bl: BatchLook) => {
    for (const m of [bl.solid, bl.glass]) {
      if (!m) continue;
      m.dispose();
      materials.delete(m);
    }
    bl.state.dispose();
  };

  const paint = (expressID: number, look: Look | undefined) => {
    const el = elements.get(expressID);
    if (!el) return;
    if (look) painted.set(expressID, look);
    else painted.delete(expressID);
    for (const inst of el.instances) write(inst, look);
  };

  const colorLook = (hex: string): Look => {
    let look = colorLooks.get(hex);
    if (!look) {
      look = lookOf(hex, 1, hex, 0.35);
      colorLooks.set(hex, look);
    }
    return look;
  };

  const applyVisible = () => {
    layer.root.visible = stationVisible && !userHidden;
  };

  const dropEdges = () => {
    for (const e of edges ?? []) {
      e.removeFromParent();
      e.geometry.dispose();
    }
    edges = null;
  };

  // Surfaces and edges swap at stage 1; edges are built the first time a station asks for them.
  // Only the shell draws edges; what sits inside it waits for the surfaces.
  const applyReveal = () => {
    const wire = reveal < 1;
    if (wire && !edges && role === 'shell') edges = layerEdges(layer, edgeMaterial);
    for (const e of edges ?? []) e.visible = wire;
    for (const b of layer.batches) b.visible = !wire;
    uniforms.uPbr.value = Math.min(1, Math.max(0, reveal - 1));
  };

  for (const b of layer.batches) adopt(b);

  return {
    elements,
    storeys,
    setClipY(y) {
      plane.constant = y;
    },
    setClipPlane(p) {
      if (p) cut.copy(p);
      if (!!p === cutOn) return;
      cutOn = !!p;
      layer.root.clippingPlanes = cutOn ? [plane, cut] : [plane];
    },
    setGhost(g) {
      if (g === lastGhost) return;
      lastGhost = g;
      if (role === 'shell') uniforms.uGhost.value = g;
      for (const [m, pass] of materials) {
        if (role === 'shell') {
          // Ghosted surfaces must not occlude what they reveal.
          if (pass !== 'solid') continue;
          m.transparent = g > 0.001;
          m.depthWrite = g < 0.5;
        } else if (metallicDucts) {
          m.emissiveIntensity = 0.3 * g;
        }
      }
    },
    setReveal(stage) {
      if (stage === reveal) return;
      reveal = stage;
      applyReveal();
    },
    setVisible(v) {
      stationVisible = v;
      applyVisible();
    },
    setUserHidden(h) {
      userHidden = h;
      applyVisible();
    },
    setTint(color, amount) {
      uniforms.uTintAmount.value = color ? Math.min(1, Math.max(0, amount)) : 0;
      if (color) uniforms.uTint.value.set(color);
    },
    setElementState(expressID, state) {
      if (current.get(expressID) === state) return;
      if (!elements.has(expressID)) return;
      current.set(expressID, state);
      paint(expressID, state ? STATE_LOOKS[state] : undefined);
    },
    clearStates() {
      for (const [id, state] of current) {
        if (state == null || colored.has(id)) continue;
        paint(id, undefined);
      }
      current.clear();
    },
    setElementColor(expressID, hex) {
      if (!elements.has(expressID)) return;
      if (hex) colored.add(expressID);
      else colored.delete(expressID);
      paint(expressID, hex ? colorLook(hex) : undefined);
    },
    clearColors() {
      for (const id of colored) paint(id, undefined);
      colored.clear();
      current.clear();
    },
    setElementVisible(expressID, visible) {
      const el = elements.get(expressID);
      if (!el) return;
      if (visible) hiddenElements.delete(expressID);
      else hiddenElements.add(expressID);
      for (const { batch, id } of el.instances) batch.setVisibleAt(id, shows(expressID));
    },
    clearVisibility() {
      for (const expressID of hiddenElements) {
        const el = elements.get(expressID);
        if (el)
          for (const { batch, id } of el.instances) batch.setVisibleAt(id, !removed.has(expressID));
      }
      hiddenElements.clear();
    },
    setRemoved(ids) {
      const next = new Set(ids);
      const changed = [...removed, ...next].filter((id) => removed.has(id) !== next.has(id));
      removed.clear();
      for (const id of next) if (elements.has(id)) removed.add(id);
      for (const expressID of changed) {
        const el = elements.get(expressID);
        if (el) for (const { batch, id } of el.instances) batch.setVisibleAt(id, shows(expressID));
      }
    },
    refresh() {
      dropEdges();
      const live = new Set(layer.batches);
      for (const [b, bl] of perBatch) {
        if (live.has(b)) continue;
        release(bl);
        perBatch.delete(b);
      }
      for (const b of layer.batches) {
        const bl = perBatch.get(b);
        if (bl) repaint(b, bl);
        else adopt(b);
      }
      applyReveal();
    },
    dispose() {
      for (const [b, bl] of perBatch) {
        release(bl);
        disposeBatch(b);
      }
      perBatch.clear();
      dropEdges();
      edgeMaterial.dispose();
      layer.root.removeFromParent();
    },
  };
}

// BatchedMesh is typed with a single material; the two-pass split hands it a pair.
function setMaterial(b: LayerBatch, m: THREE.Material | THREE.Material[]): void {
  (b as THREE.Mesh<THREE.BufferGeometry, THREE.Material | THREE.Material[]>).material = m;
}
